import { useState, useEffect } from 'react';
import { Shield, Save, AlertTriangle, Clock, Globe, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'react-toastify';
import {
  getAnomalyDetectionSettings,
  updateAnomalyDetectionSettings,
  DEFAULT_ANOMALY_SETTINGS,
  type AnomalyDetectionSettings as AnomalySettings,
  type AnomalyRuleConfig,
} from '@/lib/adminApi';

// Rule metadata for UI rendering
interface RuleMetadata {
  id: keyof AnomalySettings;
  label: string;
  description: string;
  icon: React.ReactNode;
  allowExemptions: boolean;
  thresholdConfig: {
    key: string;
    label: string;
    suffix: string;
    min: number;
    max: number;
    type?: 'number' | 'time';
  }[];
  securityNote?: string;
}

const AVAILABLE_ROLES = ['superadmin', 'admin', 'company_admin', 'group_admin'] as const;

const RULE_METADATA: RuleMetadata[] = [
  {
    id: 'failedLogins',
    label: 'Failed Login Attempts',
    description: 'Alert when multiple failed logins from the same IP.',
    icon: <AlertTriangle className="w-4 h-4 text-red-400" />,
    allowExemptions: false,
    thresholdConfig: [
      { key: 'attempts', label: 'Attempts', suffix: 'attempts', min: 1, max: 50 },
      { key: 'windowMinutes', label: 'within', suffix: 'min', min: 1, max: 60 },
    ],
    securityNote: 'Security-critical: Cannot be disabled or exempted.',
  },
  {
    id: 'bulkDeletes',
    label: 'Bulk Delete Operations',
    description: 'Alert for rapid deletion activity by a user.',
    icon: <Trash2 className="w-4 h-4 text-red-400" />,
    allowExemptions: false,
    thresholdConfig: [
      { key: 'count', label: 'Deletes', suffix: 'deletes', min: 1, max: 100 },
      { key: 'windowMinutes', label: 'within', suffix: 'min', min: 1, max: 60 },
    ],
    securityNote: 'Security-critical: Cannot be disabled or exempted.',
  },
  {
    id: 'geoAnomaly',
    label: 'Geographic Anomaly',
    description: 'Detect logins from a new country shortly after another login.',
    icon: <Globe className="w-4 h-4 text-amber-400" />,
    allowExemptions: true,
    thresholdConfig: [
      { key: 'windowHours', label: 'Within', suffix: 'hour(s)', min: 1, max: 24 },
    ],
  },
  {
    id: 'offHours',
    label: 'Off-Hours Access',
    description: 'Flag critical actions performed outside business hours.',
    icon: <Clock className="w-4 h-4 text-amber-400" />,
    allowExemptions: true,
    thresholdConfig: [
      { key: 'startHour', label: 'Start', suffix: '', min: 0, max: 23, type: 'time' },
      { key: 'endHour', label: 'End', suffix: '', min: 0, max: 23, type: 'time' },
    ],
  },
];

export function AnomalyDetectionSettings() {
  const [settings, setSettings] = useState<AnomalySettings>(DEFAULT_ANOMALY_SETTINGS);
  const [originalSettings, setOriginalSettings] = useState<AnomalySettings>(DEFAULT_ANOMALY_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await getAnomalyDetectionSettings();
      setSettings(data);
      setOriginalSettings(data);
    } catch (error: unknown) {
      console.error('Failed to load anomaly detection settings:', error);
      setSettings(DEFAULT_ANOMALY_SETTINGS);
      setOriginalSettings(DEFAULT_ANOMALY_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = () => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updated = await updateAnomalyDetectionSettings(settings);
      setSettings(updated);
      setOriginalSettings(updated);
      toast.success('Anomaly detection settings saved');
    } catch (error: unknown) {
      console.error('Failed to save settings:', error);
      const message = error instanceof Error ? error.message : 'Failed to save settings';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const updateRuleConfig = (ruleId: keyof AnomalySettings, updates: Partial<AnomalyRuleConfig>) => {
    setSettings((prev) => ({
      ...prev,
      [ruleId]: { ...prev[ruleId], ...updates },
    }));
  };

  const toggleExemptRole = (ruleId: keyof AnomalySettings, role: string) => {
    const currentExempt = settings[ruleId].exemptRoles || [];
    const newExempt = currentExempt.includes(role)
      ? currentExempt.filter((r) => r !== role)
      : [...currentExempt, role];
    updateRuleConfig(ruleId, { exemptRoles: newExempt });
  };

  const formatHour = (hour: number): string => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:00 ${period}`;
  };

  // Type-safe helper to access dynamic config values
  const getConfigValue = (config: AnomalyRuleConfig, key: string): number => {
    switch (key) {
      case 'attempts': return config.attempts ?? 0;
      case 'windowMinutes': return config.windowMinutes ?? 0;
      case 'count': return config.count ?? 0;
      case 'windowHours': return config.windowHours ?? 0;
      case 'startHour': return config.startHour ?? 0;
      case 'endHour': return config.endHour ?? 0;
      default: return 0;
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-muted rounded" />
        <div className="h-24 bg-muted rounded" />
        <div className="h-24 bg-muted rounded" />
        <div className="h-24 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white">Detection Rules</h3>
        </div>
        {hasChanges() && (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="h-8 bg-[#8FB560] hover:bg-[#7a9d51]"
          >
            <Save className="w-3 h-3 mr-1" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {RULE_METADATA.map((rule) => {
          const config = settings[rule.id];
          const _isDisabled = !rule.allowExemptions && !config.enabled;

          return (
            <div
              key={rule.id}
              className={`p-4 bg-background rounded-lg border ${
                !rule.allowExemptions
                  ? 'border-red-500/30'
                  : config.enabled
                    ? 'border-border'
                    : 'border-border opacity-60'
              }`}
            >
              {/* Header with toggle */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {rule.icon}
                  <div>
                    <h4 className="text-sm font-medium text-white">{rule.label}</h4>
                    <p className="text-xs text-muted-foreground">{rule.description}</p>
                  </div>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(enabled) => updateRuleConfig(rule.id, { enabled })}
                  disabled={!rule.allowExemptions}
                />
              </div>

              {/* Thresholds */}
              <div
                className={`flex items-center gap-2 flex-wrap ${!config.enabled ? 'opacity-50' : ''}`}
              >
                {rule.thresholdConfig.map((threshold, idx) => (
                  <div key={threshold.key} className="flex items-center gap-1">
                    {idx > 0 && threshold.label && (
                      <span className="text-xs text-muted-foreground">{threshold.label}</span>
                    )}
                    {threshold.type === 'time' ? (
                      <Input
                        type="number"
                        value={getConfigValue(config, threshold.key)}
                        onChange={(e) =>
                          updateRuleConfig(rule.id, {
                            [threshold.key]: Math.max(0, Math.min(23, parseInt(e.target.value) || 0)),
                          })
                        }
                        className="bg-card border-border h-8 w-16 text-center text-sm"
                        min={threshold.min}
                        max={threshold.max}
                        disabled={!config.enabled}
                      />
                    ) : (
                      <Input
                        type="number"
                        value={getConfigValue(config, threshold.key)}
                        onChange={(e) =>
                          updateRuleConfig(rule.id, {
                            [threshold.key]: parseInt(e.target.value) || threshold.min,
                          })
                        }
                        className="bg-card border-border h-8 w-16 text-center text-sm"
                        min={threshold.min}
                        max={threshold.max}
                        disabled={!config.enabled}
                      />
                    )}
                    {threshold.suffix && (
                      <span className="text-xs text-muted-foreground">{threshold.suffix}</span>
                    )}
                  </div>
                ))}

                {/* Display business hours summary for offHours */}
                {rule.id === 'offHours' && config.enabled && (
                  <span className="text-xs text-muted-foreground ml-2">
                    ({formatHour(config.startHour!)} - {formatHour(config.endHour!)})
                  </span>
                )}
              </div>

              {/* Role Exemptions (only for non-critical rules) */}
              {rule.allowExemptions && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">
                    Exempt roles (will not trigger this alert):
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {AVAILABLE_ROLES.map((role) => (
                      <Badge
                        key={role}
                        variant={config.exemptRoles?.includes(role) ? 'default' : 'outline'}
                        className={`cursor-pointer text-xs ${
                          config.exemptRoles?.includes(role)
                            ? 'bg-[#8FB560] hover:bg-[#7a9d51] text-white'
                            : 'hover:bg-muted'
                        } ${!config.enabled ? 'opacity-50 pointer-events-none' : ''}`}
                        onClick={() => config.enabled && toggleExemptRole(rule.id, role)}
                      >
                        {role.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Security notice for critical rules */}
              {rule.securityNote && (
                <p className="text-xs text-amber-500/80 mt-3 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  {rule.securityNote}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-4 p-3 rounded-lg bg-muted/50 border border-border">
        <p className="text-xs text-muted-foreground">
          <span className="text-amber-400 font-medium">Active rules: </span>
          {Object.values(settings).filter((r) => r.enabled).length} of 4 enabled.
          {settings.geoAnomaly.exemptRoles?.length > 0 || settings.offHours.exemptRoles?.length > 0 ? (
            <>
              {' '}Roles exempt from some checks:{' '}
              <span className="text-white">
                {[
                  ...new Set([
                    ...(settings.geoAnomaly.exemptRoles || []),
                    ...(settings.offHours.exemptRoles || []),
                  ]),
                ].join(', ')}
              </span>
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
}
